'use client';

/**
 * Where a portalled overlay has to mount so it is still visible in fullscreen.
 *
 * The Fullscreen API does not just resize an element — it promotes it into the
 * browser's **top layer**, which paints above the whole document and ignores
 * z-index entirely. Only that element's own subtree comes with it. So every
 * overlay this app portals into `<body>` (see FloatingWindow's note on why they
 * are portalled at all) is, while the canvas workspace is fullscreen, painted
 * *underneath* an opaque element covering the screen. It is mounted, focused
 * and listening for keys — you simply cannot see it.
 *
 * That is what made "Rename service" look dead in fullscreen: the prompt was
 * there, behind the diagram, waiting for a name.
 *
 * The fix is to portal into `document.fullscreenElement` while there is one, so
 * the overlay joins the subtree that is actually on screen, and back into
 * `<body>` when fullscreen ends. Nothing else changes: these overlays are
 * `position: fixed`, whose containing block stays the viewport (the workspace
 * sets no transform/filter), so their coordinates and their `overflow-hidden`
 * ancestor behave exactly as before.
 */

import { useEffect, useState } from 'react';

/** Includes the vendor-prefixed form Safari still ships. */
type FullscreenDoc = Document & { webkitFullscreenElement?: Element | null };

function currentRoot(): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  const doc = document as FullscreenDoc;
  const active = doc.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
  return active instanceof HTMLElement ? active : document.body;
}

/**
 * The element portalled overlays should mount into right now — the fullscreen
 * element if there is one, otherwise `<body>`.
 *
 * Resolved during the first render rather than in an effect, so a dialog opened
 * while already fullscreen mounts in the right place immediately. Re-portalling
 * a tick later would tear down and rebuild the DOM under it, losing the focus
 * the dialog's own mount effect had just placed in its input.
 */
export function useOverlayRoot(): HTMLElement | null {
  const [root, setRoot] = useState<HTMLElement | null>(currentRoot);

  useEffect(() => {
    const sync = () => setRoot(currentRoot());
    // Covers the case where fullscreen changed between the first render and
    // this effect, and where the element was never resolved during SSR.
    sync();
    document.addEventListener('fullscreenchange', sync);
    document.addEventListener('webkitfullscreenchange', sync);
    return () => {
      document.removeEventListener('fullscreenchange', sync);
      document.removeEventListener('webkitfullscreenchange', sync);
    };
  }, []);

  return root;
}
